import { useCallback, useState } from 'react';
import type { ZodType } from 'zod';
import { ApiError } from './api';

export type FieldErrors = Record<string, string>;

/**
 * Estado de formulário validado pelos mesmos schemas Zod da API (`packages/contracts`).
 * `submit` valida, chama `onValid` e transforma erros de validação da API em erros por campo.
 */
export function useZodForm<S extends ZodType, V extends Record<string, unknown>>(schema: S, initial: V) {
  const [values, setValues] = useState<V>(initial);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const set = useCallback(<K extends keyof V>(key: K, value: V[K]) => {
    setValues((v) => ({ ...v, [key]: value }));
    setErrors((e) => {
      if (!(key as string in e)) return e;
      const { [key as string]: _drop, ...rest } = e;
      return rest;
    });
  }, []);

  const submit = useCallback(
    (onValid: (data: S['_output']) => Promise<unknown> | unknown) => async (e?: { preventDefault(): void }) => {
      e?.preventDefault();
      setFormError(null);
      const parsed = schema.safeParse(values);
      if (!parsed.success) {
        const next: FieldErrors = {};
        for (const issue of parsed.error.issues) {
          const path = issue.path.join('.');
          if (!(path in next)) next[path] = issue.message;
        }
        setErrors(next);
        return;
      }
      setErrors({});
      setSubmitting(true);
      try {
        await onValid(parsed.data);
      } catch (err) {
        if (err instanceof ApiError) {
          const fields = err.fieldErrors();
          if (Object.keys(fields).length) setErrors(fields);
          setFormError(err.message);
        } else {
          setFormError('Algo deu errado. Tente de novo.');
        }
      } finally {
        setSubmitting(false);
      }
    },
    [schema, values],
  );

  return { values, setValues, set, errors, setErrors, formError, setFormError, submitting, submit };
}
